import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, MessageCircle, Send } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE_LINKS } from "@/content/ContentContext";
import { PRODUCTS } from "@/data/content";
import { ProductIcon } from "@/lib/icons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ full_name: "", company: "", email: "", phone: "", industry: "", employees: "", products: [], message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleProduct = (slug) =>
    setForm((f) => ({ ...f, products: f.products.includes(slug) ? f.products.filter((p) => p !== slug) : [...f.products, slug] }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lead_type: "demo" });
      setDone(true);
      toast.success(t("contact.successTitle"));
    } catch (err) {
      toast.error(t("contact.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-slate-800/70 border border-slate-700 focus:border-indigo-500 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors";

  return (
    <div data-testid="contact-page">
      <section className="relative py-20 sm:py-28 px-5 sm:px-8 grid-bg overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[500px] glow-orb pointer-events-none" />
        <div className="max-w-5xl mx-auto grid lg:grid-cols-5 gap-12 relative">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.25em] font-mono2 font-semibold text-cyan-400 mb-5">{t("contact.eyebrow")}</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-heading tracking-tight text-white mb-5">{t("contact.title")}</h1>
            <p className="text-base text-slate-400 leading-relaxed mb-10">{t("contact.sub")}</p>
            <a
              href={`https://wa.me/${SITE_LINKS.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-sales-btn"
              className="inline-flex items-center gap-2.5 rounded-full border border-emerald-500/50 text-emerald-300 font-semibold px-6 py-3 text-sm hover:bg-emerald-500/10 transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> {t("common.whatsappSales")}
            </a>
          </div>

          <div className="lg:col-span-3">
            {done ? (
              <div data-testid="contact-success" className="glass rounded-3xl p-12 text-center animate-fade-up">
                <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-6" />
                <h2 className="text-2xl font-bold font-heading text-white mb-3">{t("contact.successTitle")}</h2>
                <p className="text-sm text-slate-400 mb-8">{t("contact.successSub")}</p>
                <button
                  data-testid="contact-another"
                  onClick={() => { setDone(false); setForm({ full_name: "", company: "", email: "", phone: "", industry: "", employees: "", products: [], message: "" }); }}
                  className="text-sm font-semibold text-indigo-300 border border-indigo-500/40 rounded-full px-6 py-3 hover:bg-indigo-500/10 transition-colors"
                >
                  {t("contact.another")}
                </button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="glass rounded-3xl p-7 sm:p-9 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <input data-testid="contact-full-name" required value={form.full_name} onChange={set("full_name")} placeholder={t("contact.fullName")} className={inputCls} />
                  <input data-testid="contact-company" required value={form.company} onChange={set("company")} placeholder={t("contact.company")} className={inputCls} />
                  <input data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} placeholder={t("contact.email")} className={inputCls} />
                  <input data-testid="contact-phone" required value={form.phone} onChange={set("phone")} placeholder={t("contact.phone")} className={inputCls} />
                  <select data-testid="contact-industry" value={form.industry} onChange={set("industry")} className={inputCls}>
                    <option value="">{t("contact.industry")}</option>
                    {t("contact.industryOptions").map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <select data-testid="contact-employees" value={form.employees} onChange={set("employees")} className={inputCls}>
                    <option value="">{t("contact.employees")}</option>
                    {t("contact.employeeOptions").map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>

                <div>
                  <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 mb-3">{t("contact.products")}</p>
                  <div className="flex flex-wrap gap-2">
                    {PRODUCTS.map((p) => {
                      const on = form.products.includes(p.short);
                      return (
                        <button
                          type="button"
                          key={p.slug}
                          data-testid={`contact-product-${p.slug}`}
                          onClick={() => toggleProduct(p.short)}
                          className={`inline-flex items-center gap-2 text-xs font-medium rounded-full px-3.5 py-2 border transition-colors ${
                            on ? `${p.accent.badge} border-transparent` : "text-slate-400 border-slate-700 hover:border-slate-500"
                          }`}
                        >
                          <ProductIcon name={p.icon} className="w-3.5 h-3.5" /> {p.short}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <textarea data-testid="contact-message" value={form.message} onChange={set("message")} placeholder={t("contact.message")} rows={4} className={`${inputCls} resize-none`} />

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white font-semibold px-7 py-4 text-sm transition-colors shadow-lg shadow-indigo-500/30"
                >
                  {submitting ? t("contact.submitting") : t("contact.submit")} <Send className="w-4 h-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
